import { useEffect, useState } from "react";
import { Globe, Phone, MapPin, Camera, ChevronLeft, ChevronRight, Route as RouteIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const SHOP_AMENITY_LABELS = {
  has_ebikes: "E-bikes", has_rentals: "Rentals", has_repairs: "Repairs",
  has_water: "Water", has_restrooms: "Restrooms", has_parking: "Parking",
};

export function mapsLink(b) {
  return b && b.lat != null ? `https://www.google.com/maps/search/?api=1&query=${b.lat},${b.lng}` : null;
}

// Shared rich detail card for an attraction / advertiser / bike shop. Used by the
// Explore category list and by the trail map (tapping a pin). `routeAction` is an
// optional { shown, color, onToggle } used for attractions that have a linked route.
export default function BizDetailDialog({ biz, onClose, routeAction = null }) {
  const b = biz || {};
  const photos = ((b.photos && b.photos.length ? b.photos : [b.cover_image || b.logo]) || []).filter(Boolean);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [b.id]);
  const multi = photos.length > 1;
  const cur = photos.length ? photos[Math.min(idx, photos.length - 1)] : null;
  const next = () => setIdx((i) => (i + 1) % photos.length);
  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);
  const maps = mapsLink(b);
  const amenities = b.amenities ? Object.entries(b.amenities).filter(([, v]) => v).map(([k]) => SHOP_AMENITY_LABELS[k] || k) : [];

  return (
    <Dialog open={!!biz} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden gap-0" data-testid="rider-biz-detail">
        <div className="relative w-full h-56 bg-secondary">
          {cur ? (
            <button type="button" onClick={multi ? next : undefined} data-testid="rider-biz-detail-photo"
              aria-label={multi ? "Next photo" : undefined}
              className={`block w-full h-full ${multi ? "cursor-pointer" : "cursor-default"}`}>
              <img src={cur} alt={b.name} className="w-full h-full object-cover" />
            </button>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/35 via-primary/10 to-secondary flex items-center justify-center">
              <Camera className="w-10 h-10 text-primary/60" />
            </div>
          )}
          {multi && (
            <>
              <button type="button" onClick={prev} aria-label="Previous photo" data-testid="rider-biz-detail-prev"
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/45 text-white flex items-center justify-center hover:bg-black/65 transition-colors"><ChevronLeft className="w-5 h-5" /></button>
              <button type="button" onClick={next} aria-label="Next photo" data-testid="rider-biz-detail-next"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/45 text-white flex items-center justify-center hover:bg-black/65 transition-colors"><ChevronRight className="w-5 h-5" /></button>
              <div className="absolute top-2 right-2 rounded-full bg-black/55 text-white text-[11px] font-semibold px-2 py-0.5" data-testid="rider-biz-detail-counter">{idx + 1}/{photos.length}</div>
              <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
                {photos.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-4 bg-white" : "w-1.5 bg-white/55"}`} />)}
              </div>
            </>
          )}
        </div>
        <div className="p-5">
          <DialogHeader className="space-y-1 text-left">
            {b.category && <div className="text-[11px] text-primary font-semibold uppercase tracking-wide">{b.category}</div>}
            <DialogTitle className="font-heading font-extrabold text-xl leading-tight" data-testid="rider-biz-detail-name">{b.name || "Attraction"}</DialogTitle>
            <DialogDescription className="sr-only">Details for {b.name || "this place"}</DialogDescription>
          </DialogHeader>

          {b.short_description && <p className="text-sm text-foreground/80 mt-3 whitespace-pre-line">{b.short_description}</p>}

          {amenities.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {amenities.map((a) => <span key={a} className="rounded-full bg-primary/10 text-primary text-[11px] font-semibold px-2.5 py-1">{a}</span>)}
            </div>
          )}

          <div className="mt-4 space-y-2 text-sm">
            {b.phone && <a href={`tel:${b.phone}`} className="flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors"><Phone className="w-4 h-4 text-primary" />{b.phone}</a>}
            {b.website && <a href={b.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors break-all"><Globe className="w-4 h-4 text-primary" />{b.website.replace(/^https?:\/\//, "")}</a>}
            {maps && <a href={maps} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors"><MapPin className="w-4 h-4 text-primary" />View location on map</a>}
          </div>

          {routeAction && (
            <button type="button" onClick={routeAction.onToggle} data-testid="rider-biz-detail-route"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full border text-sm font-semibold px-4 py-2.5 active:scale-[0.98] transition-transform"
              style={routeAction.shown
                ? { background: routeAction.color || "#22D3EE", color: "#fff", borderColor: "transparent" }
                : { color: routeAction.color || "#22D3EE", borderColor: routeAction.color || "#22D3EE" }}>
              <RouteIcon className="w-4 h-4" />{routeAction.shown ? "Hide route on map" : "Show route on map"}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
